import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    AlertBanner,
    Button,
    DropdownMenu,
    Input,
    ScrollBar,
    SparkIcon,
    SparkModalPortal,
    TooltipTrigger,
    sparkToast,
} from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';
import { deploy_in_progress, undeploy_in_progress } from '@/shared/constants/taskStatuses';
import { MOVE_LOCKED_TOOLTIP } from '@/shared/utils/deploymentUx';
import { isUsecaseMoveLocked } from '@/features/tasks/utils/bulkMoveTasks';
import LanguageSelector from '../LanguageSelector';
import { sanitizeProjectName, sanitizeTaskName } from './projectNameUtils';
import { CustomerIntentModal, useCustomerIntentModal } from '@/features/customer-intents';
import { TaskTypeControls, TaskLabelsField } from '../CreationWizard/shared/TaskTypeControls';
import PriorityRanking from '../CreationWizard/businessGoals/PriorityRanking';
import ConstraintsEditor from '../CreationWizard/businessGoals/ConstraintsEditor';
import FallbackLabelSelect from '../CreationWizard/businessGoals/FallbackLabelSelect';
import LabelPrioritySelect from '../CreationWizard/businessGoals/LabelPrioritySelect';
import {
    LABEL_PRIORITY_KIND,
    labelPriorityAppliesTo,
    reconcileLabelPriority,
} from '../CreationWizard/businessGoals/labelPriority';
import {
    BEST_GUESS_VALUE,
    fallbackLabelFromStored,
    fallbackPayloadFromSelect,
    isFallbackSentinel,
    NO_PREDICTIONS_VALUE,
} from '../CreationWizard/businessGoals/fallbackLabel';
import {
    DEFAULT_PILLAR_ORDER,
    collectConstraintErrors,
    constraintDuplicateMetricErrors,
    constraintsFromBackend,
    constraintsToPayload,
    DUPLICATE_CONSTRAINT_TOAST,
} from '../CreationWizard/businessGoals/constraintOptions';
import '../CreationWizard/creation-wizard.css';
import './EditTaskModal.css';

/** Info icon with hover tooltip (replaces antd Tooltip + InfoCircleOutlined). */
const InfoTooltipIcon = ({ text }) => (
    <TooltipTrigger text={text}>
        <span className="edit-task-modal__info-icon">
            <SparkIcon slug="circle-info" size="small" />
        </span>
    </TooltipTrigger>
);

/**
 * Counts how many usecases share the same `sub_group_name` with `task` inside
 * `task.group_name`. A count > 1 means the task is a composed (multi) task.
 *
 * `allProjects` is the same `allApps` array used elsewhere on the HomePage:
 * a list of project dicts, each carrying `sub_groups[].usecases[]`.
 */
const countSiblingsInSubGroup = (task, allProjects) => {
    if (!task) return 0;
    const project = (allProjects || []).find(
        (p) => p?.group_name === task.group_name
    );
    if (!project) return 0;
    const subGroup = (project.sub_groups || []).find(
        (sg) => sg?.sub_group_name === task.sub_group_name
    );
    return (subGroup?.usecases || []).length;
};

/**
 * "Task Name" field semantics:
 *   - `umbrella` + atomic task (single usecase in sub_group): the Task Name
 *     IS the umbrella, which for atomic tasks equals `usecase_name` (and
 *     `sub_group_name`, kept in lock-step).
 *   - `umbrella` + multi task (>=2 usecases share sub_group_name): the Task
 *     Name binds to `sub_group_name` (the umbrella shared by all subtasks);
 *     individual `usecase_name`s are not edited here.
 *   - `subtask` mode (always inside a multi task): the Task Name binds to a
 *     specific subtask's `usecase_name`; the umbrella `sub_group_name` is
 *     not touched.
 *
 * `mode` defaults to `umbrella` because every entry point that does not
 * explicitly target a single subtask should be editing the umbrella.
 */
export const EditTaskModal = ({
    isOpen,
    onClose,
    projectName,
    allProjects = [],
    task,
    mode = 'umbrella',
    onUpdateTask
}) => {
    const customerIntent = useCustomerIntentModal();
    const [taskName, setTaskName] = useState('');
    const [selectedProjectName, setSelectedProjectName] = useState('');
    const [newProjectName, setNewProjectName] = useState('');
    const [projectNameError, setProjectNameError] = useState(false);
    const [taskType, setTaskType] = useState(TaskType.classification);
    const [classificationType, setClassificationType] = useState(null);
    const [taskDescription, setTaskDescription] = useState('');
    const [taskNameError, setTaskNameError] = useState(false);
    const [classificationTypeError, setClassificationTypeError] = useState(false);
    const [includeRelations, setIncludeRelations] = useState(false);
    const [classifySpeakers, setClassifySpeakers] = useState(false);
    // Fallback target: BEST_GUESS_VALUE, NO_PREDICTIONS_VALUE, a label name,
    // or null (unset). Required whenever the select is shown.
    const [fallbackLabel, setFallbackLabel] = useState(null);
    // Whether the fallback was picked explicitly (stored on the task or chosen
    // here) rather than derived from "What matters most" — same flag as the
    // Business Goals step, so dropping a choice only clears a derived fallback.
    const [fallbackLabelUserSet, setFallbackLabelUserSet] = useState(false);
    // "What matters most for these labels?" — `{ kind, label }` or null.
    // Shown (and required) exactly like in Business Goals: single-label
    // classification with exactly two labels.
    const [labelPriority, setLabelPriority] = useState(null);
    const [labelPriorityError, setLabelPriorityError] = useState(null);
    const [expectedLabels, setExpectedLabels] = useState([]);
    const [labelsError, setLabelsError] = useState(null);
    const [fallbackLabelError, setFallbackLabelError] = useState(null);
    const [priorityOrder, setPriorityOrder] = useState([...DEFAULT_PILLAR_ORDER]);
    const [constraints, setConstraints] = useState([]);
    const [constraintErrors, setConstraintErrors] = useState(null);
    const [taskLanguages, setTaskLanguages] = useState(['en']);
    const [taskLanguagesError, setTaskLanguagesError] = useState(false);

    const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);

    // Scrollable dialog body — tracked by the Spark ScrollBar, which is a
    // sibling of this node (same pattern as the Spark Modal body shell).
    const scrollBodyRef = useRef(null);

    const isMultiTask = useMemo(
        () => countSiblingsInSubGroup(task, allProjects) > 1,
        [task, allProjects]
    );
    const isUmbrellaEdit = mode !== 'subtask';
    const editsSubGroupName = isUmbrellaEdit && isMultiTask;

    const isClassification = taskType === TaskType.classification;
    const isNer = taskType === TaskType.NER;
    const showLabelsSection =
        !editsSubGroupName && (isClassification || isNer);
    // Mirrors Business Goals: the priority select takes over for single-label
    // classification with exactly two labels; options 2–5 derive the fallback
    // (the other label), so the Fallback select only stays visible for
    // "equally important" (or when the priority select does not apply).
    const showLabelPrioritySection =
        showLabelsSection &&
        isClassification &&
        labelPriorityAppliesTo({ classificationType, labels: expectedLabels });
    const showFallbackSection =
        !editsSubGroupName &&
        isClassification &&
        expectedLabels.length > 0 &&
        Boolean(classificationType) &&
        (!showLabelPrioritySection || labelPriority?.kind === LABEL_PRIORITY_KIND.equal);
    // Languages are stored per-subtask; the umbrella edit of a multi task
    // only writes to one subtask, so the field is hidden there like the
    // other per-subtask sections (type / labels / fallback).
    const showLanguagesSection = !editsSubGroupName;

    // Initialize form with task data when modal opens or task changes
    useEffect(() => {
        if (isOpen && task) {
            const initialTaskName = editsSubGroupName
                ? (task.sub_group_name || task.usecase_name || '')
                : (task.usecase_name || '');
            setTaskName(initialTaskName);
            setSelectedProjectName(task.group_name || projectName || '');
            const baseTaskType =
                task.task_type === TaskType.NER_WITH_RELATIONS
                    ? TaskType.NER
                    : (task.task_type || TaskType.classification);
            setTaskType(baseTaskType);
            setClassificationType(
                task.is_multilabel
                    ? 'multi'
                    : (baseTaskType === TaskType.classification ? 'single' : null)
            );
            setTaskDescription(task.usecase_goal || '');
            setIncludeRelations(task.task_type === TaskType.NER_WITH_RELATIONS);
            setClassifySpeakers(task.classify_speakers || false);
            const labels =
                baseTaskType === TaskType.NER
                    ? task.manually_added_entities || []
                    : task.manually_added_labels || [];
            setExpectedLabels(labels);
            const storedFallback = fallbackLabelFromStored({
                isClassification: baseTaskType === TaskType.classification,
                isMultilabel: Boolean(task.is_multilabel),
                rejectionLabelName: task.rejection_label_name,
                confidenceRejectionEnabled: task.confidence_rejection_enabled,
            });
            setFallbackLabel(storedFallback);
            // Legacy two-label tasks predate the priority choice: show them as
            // "Both labels are equally important" (the first option) so Update
            // stores it instead of demanding a pick.
            const priorityApplies =
                baseTaskType === TaskType.classification &&
                labelPriorityAppliesTo({
                    classificationType: task.is_multilabel ? 'multi' : 'single',
                    labels,
                });
            const storedLabelPriority =
                task.label_priority ||
                (priorityApplies ? { kind: LABEL_PRIORITY_KIND.equal, label: null } : null);
            setLabelPriority(storedLabelPriority);
            // A stored fallback is the user's saved pick — unless a stored
            // option 2–5 derived it (Business Goals marks those auto-managed,
            // so dropping the choice clears them). A task with none has its
            // select auto-managed until the user touches it.
            setFallbackLabelUserSet(
                Boolean(storedFallback) &&
                    (!storedLabelPriority || storedLabelPriority.kind === LABEL_PRIORITY_KIND.equal)
            );
            setPriorityOrder(
                task.business_goal_priorities?.length
                    ? [...task.business_goal_priorities]
                    : [...DEFAULT_PILLAR_ORDER]
            );
            setConstraints(constraintsFromBackend(task.constraints));
            setTaskLanguages(
                task.task_languages?.length ? [...task.task_languages] : ['en']
            );
            setTaskNameError(false);
            setClassificationTypeError(false);
            setProjectNameError(false);
            setLabelsError(null);
            setFallbackLabelError(null);
            setLabelPriorityError(null);
            setConstraintErrors(null);
            setTaskLanguagesError(false);
        }
    }, [isOpen, task, editsSubGroupName, projectName]);

    const resetForm = () => {
        setTaskName('');
        setSelectedProjectName('');
        setNewProjectName('');
        setProjectNameError(false);
        setTaskType(TaskType.classification);
        setClassificationType(null);
        setTaskDescription('');
        setTaskNameError(false);
        setClassificationTypeError(false);
        setIncludeRelations(false);
        setClassifySpeakers(false);
        setFallbackLabel(null);
        setFallbackLabelUserSet(false);
        setLabelPriority(null);
        setLabelPriorityError(null);
        setExpectedLabels([]);
        setLabelsError(null);
        setFallbackLabelError(null);
        setPriorityOrder([...DEFAULT_PILLAR_ORDER]);
        setConstraints([]);
        setConstraintErrors(null);
        setTaskLanguages(['en']);
        setTaskLanguagesError(false);
    };

    /**
     * Single entry point for type / labels / priority / fallback edits. Every
     * patch resolves the next values, runs the Business Goals reconciliation
     * (`reconcileLabelPriority`) so "What matters most" and the fallback stay
     * in lockstep, then applies this modal's own defaults.
     */
    const applyFormPatch = (patch) => {
        let nextTaskType = taskType;
        let nextClassificationType = classificationType;
        let nextLabels = expectedLabels;
        let nextFallback = fallbackLabel;
        let nextFallbackUserSet = fallbackLabelUserSet;
        let nextLabelPriority = labelPriority;

        if (patch.taskType !== undefined) {
            nextTaskType = patch.taskType;
            if (nextTaskType !== TaskType.classification) {
                nextClassificationType = null;
            }
            if (nextTaskType !== TaskType.NER) {
                setIncludeRelations(false);
            }
            setClassificationTypeError(false);
            setLabelsError(null);
            setFallbackLabelError(null);
            setLabelPriorityError(null);
        }
        if (patch.classificationType !== undefined) {
            nextClassificationType = patch.classificationType;
            setClassificationTypeError(false);
        }
        if (patch.labels !== undefined) {
            nextLabels = patch.labels;
            // Editing labels invalidates a selected fallback label that no
            // longer exists (Best guess / No predictions always stay valid).
            if (
                nextFallback &&
                !isFallbackSentinel(nextFallback) &&
                !nextLabels.includes(nextFallback)
            ) {
                nextFallback = null;
                // The explicit choice is gone — the select is auto-managed again.
                nextFallbackUserSet = false;
            }
            if (nextLabels.length) setLabelsError(null);
        }
        if (patch.labelPriority !== undefined) {
            nextLabelPriority = patch.labelPriority;
            setLabelPriorityError(null);
            // Options 2–5 derive (and hide) the fallback; "equally important"
            // re-shows it, so its error is re-evaluated on Update.
            setFallbackLabelError(null);
        }
        if (patch.fallbackLabel !== undefined) {
            nextFallback = patch.fallbackLabel;
            nextFallbackUserSet = true;
        }

        const reconciled = reconcileLabelPriority({
            taskType: nextTaskType,
            classificationType: nextClassificationType,
            labels: nextLabels,
            labelPriority: nextLabelPriority,
            fallbackLabelUserSet: nextFallbackUserSet,
        });
        if ('labelPriority' in reconciled) nextLabelPriority = reconciled.labelPriority;
        if ('fallbackLabel' in reconciled) nextFallback = reconciled.fallbackLabel;
        if ('fallbackLabelUserSet' in reconciled) {
            nextFallbackUserSet = reconciled.fallbackLabelUserSet;
        }

        const nextIsClassification = nextTaskType === TaskType.classification;
        // When no fallback label is selected yet and a label is some variation
        // of "other" (Other/OTHER/…), default the fallback to it.
        if (patch.labels !== undefined && nextIsClassification && !nextFallback) {
            const otherLabel = nextLabels.find((l) => l.toLowerCase() === 'other');
            if (otherLabel) nextFallback = otherLabel;
        }
        if (nextIsClassification && nextClassificationType === 'multi') {
            // Best guess is single-label only, so switching to multi clears it;
            // otherwise multi-label defaults a blank select to "No predictions".
            if (patch.classificationType !== undefined && nextFallback === BEST_GUESS_VALUE) {
                nextFallback = null;
                nextFallbackUserSet = false;
            } else if (
                !nextFallback &&
                (patch.classificationType !== undefined || patch.labels !== undefined)
            ) {
                nextFallback = NO_PREDICTIONS_VALUE;
            }
        }

        setTaskType(nextTaskType);
        setClassificationType(nextClassificationType);
        setExpectedLabels(nextLabels);
        setLabelPriority(nextLabelPriority);
        setFallbackLabel(nextFallback);
        setFallbackLabelUserSet(nextFallbackUserSet);
        if (nextFallback) setFallbackLabelError(null);
    };

    // The Project field moves the task. A composed task moves as one unit, so
    // any deployed (or deploying / undeploying) sibling locks it in place.
    const bundleUsecases = useMemo(() => {
        const siblings = (allProjects || [])
            .find((project) => project?.group_name === task?.group_name)
            ?.sub_groups?.find((subGroup) =>
                (subGroup?.usecases || []).some((usecase) => usecase?._id === task?._id)
            )?.usecases;
        return siblings?.length ? siblings : [task].filter(Boolean);
    }, [allProjects, task]);
    const isMoveLocked = bundleUsecases.some(isUsecaseMoveLocked);

    const handleUpdateTask = () => {
        const isDeploymentBlocked = task?.is_deployed ||
            task?.status === deploy_in_progress ||
            task?.status === undeploy_in_progress;
        const hasEmptyTaskName = !taskName.trim() && !isDeploymentBlocked;
        const hasEmptyProjectName = !selectedProjectName || !selectedProjectName.trim();
        const hasUnselectedClassificationType = !editsSubGroupName
            && taskType === TaskType.classification
            && !classificationType;
        const hasEmptyLabels = showLabelsSection && !expectedLabels.length;
        const hasEmptyLabelPriority = showLabelPrioritySection && !labelPriority;
        const hasEmptyFallback = showFallbackSection && !fallbackLabel;
        const hasEmptyLanguages =
            showLanguagesSection && (!taskLanguages || taskLanguages.length === 0);

        const nextConstraintErrors = collectConstraintErrors(constraints);
        const hasConstraintErrors = Object.keys(nextConstraintErrors).length > 0;

        setTaskNameError(hasEmptyTaskName);
        setProjectNameError(hasEmptyProjectName);
        setClassificationTypeError(hasUnselectedClassificationType);
        setLabelsError(
            hasEmptyLabels
                ? (isNer
                    ? 'Please add at least one entity type'
                    : 'Please add at least one label')
                : null
        );
        setLabelPriorityError(
            hasEmptyLabelPriority ? 'Please select what matters most for these labels' : null
        );
        setFallbackLabelError(
            hasEmptyFallback ? 'Please select a fallback label' : null
        );
        setConstraintErrors(hasConstraintErrors ? nextConstraintErrors : null);
        setTaskLanguagesError(hasEmptyLanguages);

        if (
            hasEmptyTaskName ||
            hasEmptyProjectName ||
            hasUnselectedClassificationType ||
            hasEmptyLabels ||
            hasEmptyLabelPriority ||
            hasEmptyFallback ||
            hasEmptyLanguages ||
            hasConstraintErrors
        ) {
            if (
                hasConstraintErrors &&
                Object.keys(constraintDuplicateMetricErrors(constraints)).length
            ) {
                sparkToast.error(DUPLICATE_CONSTRAINT_TOAST.title, {
                    content: DUPLICATE_CONSTRAINT_TOAST.content,
                    detailed: true,
                    duration: 5000,
                });
            }
            return;
        }

        const sanitizedName = sanitizeTaskName(taskName, { trimEnd: true });
        const { confidence_rejection_enabled: confidenceRejectionEnabled, rejection_label_name: rejectionLabelName } =
            fallbackPayloadFromSelect(fallbackLabel, {
                isClassification,
                classificationType,
            });
        // A move-locked task keeps its project whatever the dropdown holds.
        const targetGroupName = isMoveLocked
            ? (task?.group_name || '')
            : sanitizeProjectName(selectedProjectName, { trimEnd: true }).trim();

        const taskData = {
            usecase_name: isDeploymentBlocked
                ? (task?.usecase_name || taskName)
                : (editsSubGroupName
                    ? (task?.usecase_name || '')
                    : sanitizedName),
            usecase_goal: taskDescription,
            task_type: includeRelations && taskType === TaskType.NER
                ? TaskType.NER_WITH_RELATIONS
                : taskType,
            group_name: targetGroupName,
            // Edited in the "Task languages to support" field. Umbrella edits
            // of multi tasks hide that field (languages are per-subtask), so
            // they preserve the stored languages instead.
            task_languages: showLanguagesSection
                ? taskLanguages
                : (task?.task_languages?.length ? task.task_languages : ['en']),
            business_goal_priorities: priorityOrder,
            constraints: constraintsToPayload(constraints),
        };

        if (taskType === TaskType.classification) {
            taskData.is_multilabel = classificationType === 'multi';
            taskData.classify_speakers = classifySpeakers;
            taskData.confidence_rejection_enabled = confidenceRejectionEnabled;
            taskData.rejection_label_name = rejectionLabelName;
            taskData.manually_added_labels = expectedLabels;
            // Null when the select is hidden: the backend keeps the stored
            // choice and auto-clears it once the task stops qualifying.
            taskData.label_priority = showLabelPrioritySection ? labelPriority : null;
        } else if (taskType === TaskType.NER) {
            taskData.manually_added_entities = expectedLabels;
        }

        const projectChanged = targetGroupName !== (task?.group_name || '');

        if (isDeploymentBlocked) {
            taskData.sub_group_name = task?.sub_group_name || null;
        } else if (editsSubGroupName) {
            taskData.sub_group_name = sanitizedName;
            taskData.propagate_sub_group_name = true;
        } else if (isUmbrellaEdit) {
            taskData.sub_group_name = sanitizedName;
            taskData.propagate_sub_group_name = false;
        } else if (projectChanged) {
            taskData.sub_group_name = sanitizedName;
            taskData.propagate_sub_group_name = false;
        } else {
            taskData.sub_group_name = task?.sub_group_name || null;
        }

        onUpdateTask(taskData);
        resetForm();
        onClose();
    };

    const handleCancel = () => {
        onClose();
        resetForm();
    };

    const isDeploymentBlocked = task?.is_deployed ||
        task?.status === deploy_in_progress ||
        task?.status === undeploy_in_progress;

    const projectNames = [...new Set(
        (allProjects || [])
            .map((p) => p?.group_name)
            .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));

    const projectMoved =
        !isMoveLocked &&
        task &&
        selectedProjectName &&
        task.group_name &&
        selectedProjectName !== task.group_name;

    const selectProject = (name) => {
        setSelectedProjectName(name);
        setProjectNameError(false);
        setProjectDropdownOpen(false);
    };

    const createProjectFromInput = () => {
        const v = sanitizeProjectName((newProjectName || '').trim(), { trimEnd: true });
        if (!v) return;
        setSelectedProjectName(v);
        setNewProjectName('');
        setProjectNameError(false);
        setProjectDropdownOpen(false);
    };

    return (
        <SparkModalPortal
            open={isOpen}
            onClose={handleCancel}
            size="wide"
            destroyOnClose
            className="edit-task-modal-portal"
        >
            <div className="edit-task-modal-shell">
                <div className="edit-task-modal" ref={scrollBodyRef}>
                    <div className="cw-step cw-step--business-goals fs-unmask">
                    <div className="cw-title-row">
                        <div className="cw-title-row__fields">
                            <div className="edit-task-modal__name-row">
                                <input
                                    className={`cw-task-name-input${taskNameError ? ' cw-task-name-input--error' : ''}`}
                                    placeholder={isUmbrellaEdit ? 'Task name' : 'Subtask name'}
                                    value={taskName}
                                    onChange={(e) => {
                                        if (isDeploymentBlocked) return;
                                        setTaskName(sanitizeTaskName(e.target.value));
                                        setTaskNameError(false);
                                    }}
                                    disabled={isDeploymentBlocked}
                                    aria-label={isUmbrellaEdit ? 'Task name' : 'Subtask name'}
                                />
                                {isDeploymentBlocked && (
                                    <InfoTooltipIcon
                                        text={`Cannot edit ${isUmbrellaEdit ? 'task' : 'subtask'} name for deployed tasks`}
                                    />
                                )}
                                {editsSubGroupName && (
                                    <InfoTooltipIcon text="This task has multiple subtasks. Renaming it will rename all of them in lock-step." />
                                )}
                            </div>
                            {taskNameError && (
                                <div className="cw-field-error">Task name is required</div>
                            )}
                            <input
                                className="cw-task-description-input"
                                placeholder="Add task description"
                                value={taskDescription}
                                onChange={(e) => setTaskDescription(e.target.value)}
                                aria-label="Task description"
                            />
                        </div>
                    </div>

                    <div className="edit-task-modal__project">
                        <div className="cw-section-label">
                            Project name
                            <span className="cw-required-asterisk">*</span>
                            {isMoveLocked && <InfoTooltipIcon text={MOVE_LOCKED_TOOLTIP} />}
                        </div>
                        <div
                            className={`edit-task-modal__project-select${projectNameError ? ' edit-task-modal__project-select--error' : ''}`}
                        >
                            <DropdownMenu
                                className="edit-task-modal-dropdown"
                                selectType="single-select"
                                showLabel={false}
                                size="medium"
                                placeholder="Select a project"
                                text={selectedProjectName || undefined}
                                panelLayout="overlay"
                                disabled={isMoveLocked}
                                open={!isMoveLocked && projectDropdownOpen}
                                onOpenChange={setProjectDropdownOpen}
                                searchable
                                searchMode="trigger"
                                searchEmptyText="No matching projects"
                                items={projectNames.map((name) => ({
                                    label: name,
                                    state: name === selectedProjectName ? 'selected' : 'default',
                                    onClick: () => selectProject(name),
                                }))}
                                footer={(
                                    <div className="edit-task-modal-create-project">
                                        <Input
                                            className="edit-task-modal-field"
                                            showLabel={false}
                                            showHelperText={false}
                                            placeholder="Create new project name"
                                            value={newProjectName}
                                            onChange={(e) => setNewProjectName(sanitizeProjectName(e.target.value))}
                                            onKeyDown={(e) => {
                                                if (e.key !== 'Enter') return;
                                                e.stopPropagation();
                                                createProjectFromInput();
                                            }}
                                        />
                                        <Button variant="primary" onClick={createProjectFromInput}>
                                            Add
                                        </Button>
                                    </div>
                                )}
                            />
                        </div>
                        {projectNameError && (
                            <div className="cw-field-error">Project name is required</div>
                        )}
                        {projectMoved && (
                            <AlertBanner
                                feedback="warning"
                                text={(
                                    <span>
                                        This task will be moved from <b>{task.group_name}</b> to{' '}
                                        <b>{selectedProjectName}</b>.
                                    </span>
                                )}
                                showButton={false}
                                showCloseButton={false}
                            />
                        )}
                    </div>

                    {!editsSubGroupName && (
                        <TaskTypeControls
                            taskType={taskType}
                            classificationType={classificationType}
                            onPatch={applyFormPatch}
                            explainerText="Depending on the task, you'll get one specific result: category tags (for classification), specific words pulled from the text (for entity extraction), a quick written summary (for summarization), or a free-form written response (for content generation)."
                            classificationTypeError={
                                classificationTypeError
                                    ? 'Please select Yes or No'
                                    : null
                            }
                            onContactUs={customerIntent.openModal}
                        />
                    )}

                    {showLabelsSection && (
                        <div className="cw-labels-fallback-row">
                            <TaskLabelsField
                                taskType={taskType}
                                labels={expectedLabels}
                                onPatch={applyFormPatch}
                                error={labelsError}
                            />
                            {showLabelPrioritySection && (
                                <LabelPrioritySelect
                                    labels={expectedLabels}
                                    value={labelPriority}
                                    onChange={(choice) => applyFormPatch({ labelPriority: choice })}
                                    error={labelPriorityError}
                                />
                            )}
                            {showFallbackSection && (
                                <FallbackLabelSelect
                                    labels={expectedLabels}
                                    classificationType={classificationType}
                                    value={fallbackLabel}
                                    onChange={(next) => applyFormPatch({ fallbackLabel: next })}
                                    error={fallbackLabelError}
                                />
                            )}
                        </div>
                    )}

                    {showLanguagesSection && (
                        <div className="edit-task-modal__languages">
                            <LanguageSelector
                                value={taskLanguages || []}
                                onChange={(languages) => {
                                    setTaskLanguages(languages);
                                    setTaskLanguagesError(false);
                                }}
                                hasError={taskLanguagesError}
                                required={true}
                            />
                        </div>
                    )}

                    <div className="cw-goals-section">
                        <h3 className="cw-goals-section__title">
                            Define &amp; Prioritize Business Goals
                        </h3>
                        <p className="cw-goals-section__subtitle">
                            Drag and drop to rank your priorities. specific.ai will optimize the
                            model based on these trade-offs.
                        </p>
                        <PriorityRanking order={priorityOrder} onChange={setPriorityOrder} />
                    </div>

                    <ConstraintsEditor
                        constraints={constraints}
                        onChange={(next) => {
                            setConstraints(next);
                            if (constraintErrors) {
                                const collected = collectConstraintErrors(next);
                                const stillInvalid = {};
                                next.forEach((c) => {
                                    if (constraintErrors[c.id] && collected[c.id]) {
                                        stillInvalid[c.id] = collected[c.id];
                                    }
                                });
                                setConstraintErrors(
                                    Object.keys(stillInvalid).length ? stillInvalid : null
                                );
                            }
                        }}
                        isNerTask={isNer}
                        entityTypeOptions={expectedLabels}
                        errors={constraintErrors}
                    />
                    </div>
                </div>
                <ScrollBar
                    containerRef={scrollBodyRef}
                    className="edit-task-modal__scrollbar"
                />
            </div>
            <div className="edit-task-modal__footer">
                <Button variant="secondary" size="medium" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button variant="primary" size="medium" disableWhenReadOnly onClick={handleUpdateTask}>
                    Update Task
                </Button>
            </div>
            <CustomerIntentModal
                open={customerIntent.open}
                onClose={customerIntent.closeModal}
                initialSelection={customerIntent.initialSelection}
            />
        </SparkModalPortal>
    );
};
